import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotification } from '../../components/ui/NotificationToast';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import ProjectFormHeader from './components/ProjectFormHeader';
import LanguageTabs from './components/LanguageTabs';
import ProjectBasicInfo from './components/ProjectBasicInfo';
import ProjectDescription from './components/ProjectDescription';
import ProjectTimeline from './components/ProjectTimeline';
import ProjectMedia from './components/ProjectMedia';
import ProjectPreview from './components/ProjectPreview';
import { projectsService } from '../../services/dataService';

const ProjectForm = () => {
  const { success, error, warning } = useNotification();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  
  // UI State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('ar');
  const [completedLanguages, setCompletedLanguages] = useState(['ar']);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    title: {
      ar: '',
      en: '',
      tr: ''
    },
    description: {
      ar: '',
      en: '',
      tr: ''
    },
    status: 'planned',
    priority: 'medium',
    category: '',
    memberCount: 0,
    budget: 0,
    usedBudget: 0,
    tags: '',
    startDate: '',
    endDate: '',
    progress: 0,
    lastUpdated: '',
    nextReview: '',
    plannedHours: 0,
    usedHours: 0,
    featuredImage: '',
    featuredImageAlt: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Mock data
  const mockCategories = [
    { value: 'education', label: 'التعليم والتدريب', description: 'مشاريع تعليمية وتدريبية' },
    { value: 'community', label: 'خدمة المجتمع', description: 'مشاريع خدمية واجتماعية' },
    { value: 'culture', label: 'الثقافة والفنون', description: 'مشاريع ثقافية وفنية' },
    { value: 'technology', label: 'التكنولوجيا', description: 'مشاريع تقنية ورقمية' },
    { value: 'health', label: 'الصحة والرياضة', description: 'مشاريع صحية ورياضية' },
    { value: 'environment', label: 'البيئة', description: 'مشاريع بيئية ومستدامة' },
    { value: 'research', label: 'البحث والتطوير', description: 'مشاريع بحثية وتطويرية' }
  ];

  // Load existing project if ID is provided
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) return;

      setIsLoading(true);
      try {
        const project = await projectsService.getById(projectId);
        
        // Transform database format to component format
        setFormData({
          title: {
            ar: project?.titleAr || '',
            en: project?.titleEn || '',
            tr: project?.titleTr || ''
          },
          description: {
            ar: project?.descriptionAr || '',
            en: project?.descriptionEn || '',
            tr: project?.descriptionTr || ''
          },
          status: project?.status || 'planned',
          priority: 'medium',
          category: project?.categoryAr || project?.categoryEn || project?.categoryTr || '',
          memberCount: project?.memberCount || 0,
          budget: 0,
          usedBudget: 0,
          tags: '',
          startDate: project?.startDate || '',
          endDate: project?.endDate || '',
          progress: project?.progress || 0,
          lastUpdated: '',
          nextReview: '',
          plannedHours: 0,
          usedHours: 0,
          featuredImage: project?.imageUrl || '',
          featuredImageAlt: ''
        });

        setIsEditing(true);
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Error loading project:', err);
        error('فشل تحميل المشروع');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (hasUnsavedChanges) {
        handleAutoSave();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [hasUnsavedChanges, formData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key) {
          case 'k':
            e?.preventDefault();
            setIsSearchOpen(true);
            break;
          case 's':
            e?.preventDefault();
            handleSave('draft');
            break;
          case 'p':
            e?.preventDefault();
            setIsPreviewOpen(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = Object.keys(formData)?.some(key => {
      if (typeof formData?.[key] === 'object' && formData?.[key] !== null) {
        return Object.values(formData?.[key])?.some(value => value !== '');
      }
      return formData?.[key] !== '' && formData?.[key] !== 0;
    });
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // Language completion tracking
  useEffect(() => {
    const checkLanguageCompletion = () => {
      const completed = [];
      ['ar', 'en', 'tr']?.forEach(lang => {
        if (formData?.title?.[lang] && formData?.description?.[lang]) {
          completed?.push(lang);
        }
      });
      setCompletedLanguages(completed);
    };

    checkLanguageCompletion();
  }, [formData?.title, formData?.description]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error for this field
    if (errors?.[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Title validation
    if (!formData?.title?.[activeLanguage]) {
      newErrors.title = 'عنوان المشروع مطلوب';
    } else if (formData?.title?.[activeLanguage]?.length < 5) {
      newErrors.title = 'عنوان المشروع يجب أن يكون 5 أحرف على الأقل';
    }
    
    // Description validation
    if (!formData?.description?.[activeLanguage]) {
      newErrors.description = 'وصف المشروع مطلوب';
    } else if (formData?.description?.[activeLanguage]?.length < 50) {
      newErrors.description = 'وصف المشروع يجب أن يكون 50 حرف على الأقل';
    }
    
    // Status validation
    if (!formData?.status) {
      newErrors.status = 'حالة المشروع مطلوبة';
    }
    
    // Date validation
    // if (!formData?.startDate) {
    //   newErrors.startDate = 'تاريخ البداية مطلوب';
    // }
    
    // if (!formData?.endDate) {
    //   newErrors.endDate = 'تاريخ النهاية مطلوب';
    // } else if (formData?.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
    //   newErrors.endDate = 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية';
    // }
    
    // Member count validation
    // if (formData?.memberCount < 1) {
    //   newErrors.memberCount = 'عدد الأعضاء يجب أن يكون 1 على الأقل';
    // }
    
    // Budget validation
    // if (formData?.usedBudget > formData?.budget) {
    //   newErrors.usedBudget = 'الميزانية المستخدمة لا يمكن أن تتجاوز الميزانية المخططة';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const transformProjectData = () => {
    // Get category label for multilingual support
    const categoryObj = mockCategories.find(cat => cat.value === formData?.category);
    const categoryLabel = categoryObj?.label || formData?.category || '';

    // Provide default dates if not provided (since timeline fields are commented out)
    const defaultStartDate = formData?.startDate || new Date().toISOString().split('T')[0];
    const defaultEndDate = formData?.endDate || (() => {
      const date = new Date();
      date.setDate(date.getDate() + 30); // Default to 30 days from now
      return date.toISOString().split('T')[0];
    })();

    return {
      titleAr: formData?.title?.ar || '',
      titleEn: formData?.title?.en || '',
      titleTr: formData?.title?.tr || '',
      descriptionAr: formData?.description?.ar || '',
      descriptionEn: formData?.description?.en || '',
      descriptionTr: formData?.description?.tr || '',
      categoryAr: categoryLabel,
      categoryEn: categoryLabel,
      categoryTr: categoryLabel,
      imageUrl: formData?.featuredImage || '',
      status: formData?.status || 'planned',
      progress: formData?.progress || 0,
      memberCount: formData?.memberCount || 0,
      startDate: defaultStartDate,
      endDate: defaultEndDate
    };
  };

  const handleAutoSave = async () => {
    if (!hasUnsavedChanges) return;

    try {
      const projectData = transformProjectData();
      
      if (projectId) {
        // Update existing project
        await projectsService.update(projectId, projectData);
      } else {
        // Create new project as draft
        const newProject = await projectsService.create({
          ...projectData,
          status: 'planned' // Auto-save as planned/draft
        });
        
        // Update URL with new project ID
        window.history.replaceState({}, '', `/project-form?id=${newProject?.id}`);
      }
      
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const handleSave = async (type = 'draft') => {
    if (!validateForm()) {
      error('يرجى تصحيح الأخطاء قبل الحفظ');
      return;
    }

    setIsSaving(true);
    
    try {
      const projectData = transformProjectData();
      
      // Set status based on type
      if (type === 'publish') {
        projectData.status = 'ongoing'; // or 'completed' based on your needs
      } else {
        projectData.status = 'planned'; // draft status
      }
      
      let savedProject;
      if (projectId) {
        // Update existing project
        savedProject = await projectsService.update(projectId, projectData);
      } else {
        // Create new project
        savedProject = await projectsService.create(projectData);
        
        // Update URL with new project ID
        window.history.replaceState({}, '', `/project-form?id=${savedProject?.id}`);
        setIsEditing(true);
      }
      
      // Clear draft
      localStorage.removeItem('projectFormDraft');
      
      setHasUnsavedChanges(false);
      
      if (type === 'publish') {
        success('تم نشر المشروع بنجاح');
        setTimeout(() => {
          window.location.href = '/projects-management';
        }, 1500);
      } else {
        success('تم حفظ المشروع كمسودة');
      }
      
    } catch (err) {
      console.error('Save failed:', err);
      error('حدث خطأ أثناء حفظ المشروع');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    if (!formData?.title?.[activeLanguage]) {
      warning('يرجى إضافة عنوان للمشروع قبل المعاينة');
      return;
    }
    setIsPreviewOpen(true);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('لديك تغييرات غير محفوظة. هل تريد المغادرة؟');
      if (!confirmed) return;
    }
    
    // Clear draft
    localStorage.removeItem('projectFormDraft');
    window.location.href = '/projects-management';
  };

  const handleLanguageChange = (language) => {
    setActiveLanguage(language);
  };

  // Load draft on component mount
  useEffect(() => {
    const draft = localStorage.getItem('projectFormDraft');
    if (draft) {
      try {
        const { formData: draftData, timestamp } = JSON.parse(draft);
        const draftAge = Date.now() - new Date(timestamp)?.getTime();
        
        // Only load draft if it's less than 24 hours old
        if (draftAge < 24 * 60 * 60 * 1000) {
          const shouldRestore = window.confirm('تم العثور على مسودة محفوظة. هل تريد استعادتها؟');
          if (shouldRestore) {
            setFormData(draftData);
            success('تم استعادة المسودة المحفوظة');
          }
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل المشروع...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header 
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
        isMenuOpen={isMenuOpen}
      />
      {/* Sidebar */}
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      {/* Main Content */}
      <main className={`
        transition-all duration-300 pt-16
        ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}
      `}>
        {/* Form Header */}
        <ProjectFormHeader
          isEditing={isEditing}
          projectTitle={formData?.title?.[activeLanguage]}
          onSave={handleSave}
          onPreview={handlePreview}
          onCancel={handleCancel}
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Language Tabs */}
          <LanguageTabs
            activeLanguage={activeLanguage}
            onLanguageChange={handleLanguageChange}
            completedLanguages={completedLanguages}
          />

          {/* Form Sections */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="xl:col-span-2 space-y-6">
              <ProjectBasicInfo
                formData={formData}
                onFieldChange={handleFieldChange}
                activeLanguage={activeLanguage}
                errors={errors}
                categories={mockCategories}
              />

              <ProjectDescription
                formData={formData}
                onFieldChange={handleFieldChange}
                activeLanguage={activeLanguage}
                errors={errors}
              />

              {/* <ProjectTimeline
                formData={formData}
                onFieldChange={handleFieldChange}
                errors={errors}
              /> */}
            </div>

            {/* Right Column - Media & Settings */}
            <div className="space-y-6">
              <ProjectMedia
                formData={formData}
                onFieldChange={handleFieldChange}
                errors={errors}
              />

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground font-arabic mb-4">
                  إجراءات سريعة
                </h3>
                
                <div className="space-y-3">
                  <button
                    onClick={handlePreview}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                      <span className="text-accent">👁️</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium">معاينة المشروع</p>
                      <p className="text-xs text-muted-foreground">Ctrl+P</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleSave('draft')}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <span className="text-secondary">💾</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium">حفظ كمسودة</p>
                      <p className="text-xs text-muted-foreground">Ctrl+S</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="text-primary">🔍</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium">البحث السريع</p>
                      <p className="text-xs text-muted-foreground">Ctrl+K</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Form Progress */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground font-arabic mb-4">
                  تقدم النموذج
                </h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">اكتمال النموذج</span>
                    <span className="text-foreground font-medium">
                      {Math.round((completedLanguages?.length / 3) * 100)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(completedLanguages?.length / 3) * 100}%` }}
                    />
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    {completedLanguages?.length}/3 لغات مكتملة
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Project Preview Modal */}
      <ProjectPreview
        formData={formData}
        activeLanguage={activeLanguage}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
      {/* Search Command Palette */}
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
      {/* Quick Action Button */}
      <QuickActionButton />
    </div>
  );
};

export default ProjectForm;